import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Model from "@tripian/model";
import { api } from "@tripian/core";

export interface TourSearchListFilters {
  /** /tour-api/categories'den gelen kategori tag'leri; virgülle birleştirilip gönderilir */
  categoryTags: string[];
  providerIds: number[];
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  /** Dakika cinsinden (ürünlerin `duration` alanı da dakika) */
  minDuration?: number;
  maxDuration?: number;
  keywords?: string;
  sortingBy?: string;
  sortingType?: "ascending" | "descending";
}

export interface UseTourSearchListParams {
  cityId?: string | number;
  lat?: number;
  lng?: number;
  date?: string;
  to?: string;
  adults?: number;
  children?: number;
  currency?: string;
  lang?: string;
  filters: TourSearchListFilters;
  limit?: number;
}

/**
 * POST /tour-api/search üzerinden filtrelenebilir, offset tabanlı tur listesi.
 *
 * Filtreleme, arama ve sıralama tamamen sunucu tarafında: kategori tag'leri,
 * fiyat/puan/süre aralıkları, sağlayıcı seçimi ve sıralama parametre olarak
 * gidiyor. Arama parametrelerinden herhangi biri değişince liste sıfırlanıp ilk
 * sayfa yeniden çekilir; `loadMore` sonraki sayfayı mevcut listeye ekler.
 */
const useTourSearchList = (params: UseTourSearchListParams) => {
  const {
    cityId,
    lat,
    lng,
    date,
    to,
    adults = 1,
    children,
    currency = "usd",
    lang = "en",
    filters,
    limit = 10,
  } = params;

  const [products, setProducts] = useState<Model.TourSearchProduct[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();

  const offsetRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  // Gecikmiş yanıtların yeni aramanın sonuçlarının üzerine yazmasını engeller
  const generationRef = useRef<number>(0);

  /**
   * Yalnızca kullanıcı yan panelden sağlayıcı seçtiyse gönderilir. Backend
   * `if provider_ids:` ile davrandığı için (tourapi_controller.py:2109) parametre
   * yokken hiç filtre uygulanmaz — yani "tümü" anlamına gelir ve isteği şişirmeden
   * aynı sonucu verir.
   */
  const providerId = useMemo(
    () =>
      filters.providerIds.length > 0
        ? filters.providerIds.join(",")
        : undefined,
    [filters.providerIds]
  );

  const isActive =
    typeof window !== "undefined" &&
    !!window.tconfig?.SHOW_TOURS_AND_TICKETS &&
    !!cityId;

  const buildRequest = useCallback(
    (offset: number): Partial<Model.TourSearchRequest> => {
      // Geçmiş tarihle arama boş döner; diğer tur hook'ları gibi bugüne çekiyoruz
      const today = moment().startOf("day");
      const clampedDate =
        date && moment(date).isBefore(today)
          ? today.format("YYYY-MM-DD")
          : date || today.format("YYYY-MM-DD");

      return {
        cityId,
        ...(providerId ? { providerId } : {}),
        ...(lat !== undefined ? { lat } : {}),
        ...(lng !== undefined ? { lng } : {}),
        date: clampedDate,
        ...(to ? { to } : {}),
        adults,
        ...(children ? { children } : {}),
        currency,
        lang,
        instantAvailability: window.tconfig?.TOUR_INSTANT_AVAILABILITY ? 1 : 0,
        ...(filters.categoryTags.length > 0
          ? { categories: filters.categoryTags.join(",") }
          : {}),
        ...(filters.keywords ? { keywords: filters.keywords } : {}),
        ...(filters.minPrice !== undefined ? { minPrice: filters.minPrice } : {}),
        ...(filters.maxPrice !== undefined ? { maxPrice: filters.maxPrice } : {}),
        ...(filters.minRating !== undefined
          ? { minRating: filters.minRating }
          : {}),
        ...(filters.minDuration !== undefined
          ? { minDuration: filters.minDuration }
          : {}),
        ...(filters.maxDuration !== undefined
          ? { maxDuration: filters.maxDuration }
          : {}),
        ...(filters.sortingBy ? { sortingBy: filters.sortingBy } : {}),
        ...(filters.sortingType ? { sortingType: filters.sortingType } : {}),
        offset,
        limit,
      };
    },
    [
      cityId,
      providerId,
      lat,
      lng,
      date,
      to,
      adults,
      children,
      currency,
      lang,
      filters,
      limit,
    ]
  );

  const fetchPage = useCallback(
    async (offset: number, generation: number) => {
      if (!isActive) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isFirstPage = offset === 0;
      if (isFirstPage) setLoading(true);
      else setLoadingMore(true);
      setError(undefined);

      try {
        const result = await api.tourSearch(
          buildRequest(offset) as Model.TourSearchRequest
        );

        if (generation === generationRef.current) {
          const batch = result.products || [];
          setProducts(prev => (isFirstPage ? batch : [...prev, ...batch]));
          setTotal(result.total || 0);
          // Tam sayfa geldiyse arkada daha ürün olabilir. Math.min: instantAvailability
          // açıkken API sayfa boyunu 10'a kırpıyor, limit 10'dan büyük verilirse dönen
          // sayfa hiç "tam" sayılmaz ve sayfalama ilk turda dururdu.
          setHasMore(batch.length > 0 && batch.length >= Math.min(limit, 10));
          // Offset dönen ürün sayısı kadar ilerler (limit 10 iken 0, 10, 20, …)
          offsetRef.current = offset + batch.length;
        }
      } catch (err: any) {
        console.error("Tour search list Error:", err);
        if (generation === generationRef.current) {
          if (isFirstPage) {
            setProducts([]);
            setTotal(0);
          }
          setHasMore(false);
          setError(err?.message || "Tour search failed");
        }
      }

      inFlightRef.current = false;
      if (isFirstPage) setLoading(false);
      else setLoadingMore(false);
    },
    [isActive, buildRequest, limit]
  );

  // Arama parametreleri (şehir, tarih, yolcu, filtreler) değişince baştan çek
  const searchKey = useMemo(
    () =>
      JSON.stringify({
        cityId,
        providerId,
        lat,
        lng,
        date,
        to,
        adults,
        children,
        currency,
        lang,
        filters,
        limit,
      }),
    [
      cityId,
      providerId,
      lat,
      lng,
      date,
      to,
      adults,
      children,
      currency,
      lang,
      filters,
      limit,
    ]
  );

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;

    offsetRef.current = 0;
    inFlightRef.current = false;
    setProducts([]);
    setTotal(0);
    setHasMore(false);

    if (!isActive) {
      setLoading(false);
      return;
    }

    fetchPage(0, generation);
    // fetchPage kimliği zaten tüm parametrelere bağlı; searchKey onu tek bir
    // değere indirip aynı aramanın tekrar tetiklenmesini engelliyor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey, isActive]);

  const loadMore = useCallback(() => {
    if (!hasMore || inFlightRef.current) return;
    fetchPage(offsetRef.current, generationRef.current);
  }, [hasMore, fetchPage]);

  return { products, total, loading, loadingMore, hasMore, error, loadMore };
};

export default useTourSearchList;
